/**
 * AttachMenu — bottom sheet that opens when Mark taps the paperclip.
 *
 * Five quick actions + a recent-images strip. Replaces the old "tap
 * paperclip → OS picker dance immediately" path so phone use feels less
 * like wrangling Android.
 *
 * Quick actions:
 *   - Camera     → file input with capture="environment" (rear camera)
 *   - Gallery    → photo picker
 *   - Video      → video picker
 *   - File       → any file, multi-select
 *   - Paste      → read clipboard, find first image, pass through
 *
 * Recent strip: thumbnails of images Mark has previously attached, served
 * out of an IDB-backed LRU cache (lib/recentImages.ts). Tapping a thumb
 * skips every picker and goes straight into the existing _processFiles
 * pipeline. Long-press starts photo selection; taps then toggle the batch.
 */
import { useEffect, useRef, useState, type SyntheticEvent } from 'react'
import {
  X, Camera, ImagesIcon, Video, ClipboardPaste, FileText, Check,
} from 'lucide-react'
import { clsx } from 'clsx'
import { useLongPress } from '../lib/longPress'
import {
  listRecentImages, loadRecentImageFile,
  type RecentImage,
} from '../lib/recentImages'
import {
  AgentChatGallery, isNativeGalleryAvailable, nativeImageToFile,
  type NativeGalleryItem,
} from '../lib/nativeGallery'

type Props = {
  open: boolean
  /** Called with whichever files Mark picked. May be one or many. */
  onPick: (files: File[]) => void
  onClose: () => void
}

type ClipboardThumb = {
  url: string
  blob: Blob
  mimeType: string
  filename: string
}

type PhotoChoice = {
  id: string
  load: () => Promise<File | null>
}

const OPEN_GESTURE_GUARD_MS = 240

// The "File" tile is the general file/documents opt-in (Camera/Gallery tiles
// stay image-only so Android routes through the system Photo Picker). It
// accepts ANY file on purpose: there is no server-side type gate — uploads
// are classified by MIME type, and the only real limit is the
// 1 GB size cap — so a curated extension/MIME allow-list only ever greyed out
// files Mark legitimately wanted (Office docs, CAD .dwg/.rvt/.ifc, archives,
// data files, code) with zero benefit. '*/*' greys out nothing.
const FILE_PICKER_ACCEPT = '*/*'

export default function AttachMenu({ open, onPick, onClose }: Props) {
  const [recents, setRecents] = useState<RecentImage[] | null>(null)
  const [clipboard, setClipboard] = useState<ClipboardThumb | null>(null)
  const [pasteError, setPasteError] = useState<string | null>(null)
  const [actionsArmed, setActionsArmed] = useState(false)
  // null is one-tap mode; an empty array keeps selection mode active.
  const [selectedPhotos, setSelectedPhotos] = useState<PhotoChoice[] | null>(null)
  const [photoError, setPhotoError] = useState<string | null>(null)
  const [photoLoading, setPhotoLoading] = useState(false)
  const photoRequestRef = useRef(0)
  const photoBusyRef = useRef(false)

  useEffect(() => {
    setSelectedPhotos(null)
    setPhotoError(null)
    setPhotoLoading(false)
    photoBusyRef.current = false
    return () => { photoRequestRef.current += 1 }
  }, [open])

  function closeMenu() {
    photoRequestRef.current += 1
    onClose()
  }

  function selectPhoto(photo: PhotoChoice) {
    if (photoBusyRef.current) return
    setPhotoError(null)
    setSelectedPhotos((prev) => prev?.some((p) => p.id === photo.id)
      ? prev
      : [...(prev ?? []), photo])
  }

  function tapPhoto(photo: PhotoChoice) {
    if (photoBusyRef.current) return
    if (selectedPhotos === null) {
      void attachPhotos([photo])
      return
    }
    setPhotoError(null)
    setSelectedPhotos((prev) => prev?.some((p) => p.id === photo.id)
      ? prev.filter((p) => p.id !== photo.id)
      : [...(prev ?? []), photo])
  }

  async function attachPhotos(photos: PhotoChoice[]) {
    if (photoBusyRef.current || photos.length === 0) return
    photoBusyRef.current = true
    const request = ++photoRequestRef.current
    setPhotoLoading(true)
    setPhotoError(null)
    try {
      const files: File[] = []
      for (const photo of photos) {
        const file = await photo.load()
        if (request !== photoRequestRef.current) return
        if (!file) throw new Error('A selected photo is no longer available. Deselect it and try again.')
        files.push(file)
      }
      onPick(files)
      closeMenu()
    } catch (err) {
      if (request === photoRequestRef.current) {
        setPhotoError(err instanceof Error ? err.message : 'Could not load photos. Please try again.')
      }
    } finally {
      if (request === photoRequestRef.current) {
        photoBusyRef.current = false
        setPhotoLoading(false)
      }
    }
  }

  function photoButton(photo: PhotoChoice, src: string, alt: string, fromClipboard = false) {
    return <PhotoButton
      key={photo.id}
      src={src}
      alt={alt}
      clipboard={fromClipboard}
      selected={selectedPhotos === null ? undefined : selectedPhotos.some((p) => p.id === photo.id)}
      disabled={photoLoading}
      onSelect={() => selectPhoto(photo)}
      onClick={() => tapPhoto(photo)}
    />
  }
  // Device gallery row — populated only inside the Capacitor wrapper.
  // Plain PWA users get an empty array and the row never renders.
  const [gallery, setGallery] = useState<NativeGalleryItem[]>([])
  const [galleryLoading, setGalleryLoading] = useState(false)
  const [galleryDenied, setGalleryDenied] = useState(false)
  const [galleryDiag, setGalleryDiag] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setActionsArmed(false)
      return
    }
    setActionsArmed(false)
    const timer = window.setTimeout(() => setActionsArmed(true), OPEN_GESTURE_GUARD_MS)
    return () => window.clearTimeout(timer)
  }, [open])

  function suppressOpeningGesture(event: SyntheticEvent) {
    if (actionsArmed) return
    event.preventDefault()
    event.stopPropagation()
  }

  // Pull the recent strip on mount. The list is small (≤8) so we just
  // load it eagerly — no skeleton flash, no virtualization.
  useEffect(() => {
    let cancelled = false
    listRecentImages().then((items) => {
      if (cancelled) return
      setRecents(items)
    })
    return () => {
      cancelled = true
      // Revoke thumbnail URLs we created in listRecentImages so we don't
      // leak object URLs when the menu unmounts.
      setRecents((prev) => {
        if (prev) for (const r of prev) URL.revokeObjectURL(r.thumbUrl)
        return prev
      })
    }
  }, [])

  // Best-effort clipboard preview. Only runs if clipboard-read is
  // ALREADY granted — we never auto-prompt for permission, since that
  // would surface a popup every time Mark taps the paperclip. He gets
  // a thumbnail when he's already opted in (typical for installed PWAs)
  // or via the explicit Paste button otherwise.
  useEffect(() => {
    let cancelled = false
    let createdUrl: string | null = null
    ;(async () => {
      try {
        const cb = navigator.clipboard
        if (!cb || typeof cb.read !== 'function') return
        // Permissions API is the only way to check clipboard access
        // without triggering a UI prompt. Some browsers don't support
        // 'clipboard-read' as a PermissionName — skip silently.
        const perms = navigator.permissions
        if (perms?.query) {
          try {
            const status = await perms.query({ name: 'clipboard-read' as PermissionName })
            if (status.state !== 'granted') return
          } catch {
            return  // unsupported — be safe and skip
          }
        }
        const items = await cb.read()
        for (const item of items) {
          for (const type of item.types) {
            if (!type.startsWith('image/')) continue
            const blob = await item.getType(type)
            if (cancelled) return
            const url = URL.createObjectURL(blob)
            createdUrl = url
            const ext = type.split('/')[1] || 'png'
            setClipboard({
              url, blob, mimeType: type,
              filename: `clipboard-${Date.now()}.${ext}`,
            })
            return
          }
        }
      } catch {/* permission denied / no clipboard / unsupported */}
    })()
    return () => {
      cancelled = true
      if (createdUrl) URL.revokeObjectURL(createdUrl)
    }
  }, [])

  // Native gallery row — only ever populates inside the Capacitor APK
  // (`isNativeGalleryAvailable()` returns false on the plain PWA, so
  // listRecentImages remains the only source there). On first open we
  // request photos permission if we don't yet have it.
  useEffect(() => {
    let cancelled = false
    if (!open) return
    if (!isNativeGalleryAvailable()) return
    setGalleryLoading(true)
    ;(async () => {
      try {
        const res = await AgentChatGallery.getRecentImages({ limit: 16 })
        if (cancelled) return
        if (!res.permissionGranted) {
          setGalleryDenied(true)
          return
        }
        setGallery(res.images)
        if (res.images.length === 0) {
          setGalleryDiag(`raw:${res.rawCount ?? '?'} err:${res.firstError ?? 'none'}`)
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err)
        console.warn('native gallery fetch failed:', msg)
        if (!cancelled) setGalleryDiag(`throw:${msg}`)
      } finally {
        if (!cancelled) setGalleryLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [open])

  async function requestGalleryPermission() {
    try {
      const res = await AgentChatGallery.requestPermission()
      if (res.state === 'granted') {
        setGalleryDenied(false)
        const list = await AgentChatGallery.getRecentImages({ limit: 16 })
        setGallery(list.images)
      }
    } catch (err) {
      console.warn('permission request failed:', err)
    }
  }

  // Esc closes; lock body scroll while open so the chat behind doesn't
  // snap-scroll on touchmove.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        photoRequestRef.current += 1
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [onClose, open])

  function pickViaInput(
    accept: string,
    options?: { capture?: 'environment' | 'user'; multiple?: boolean },
  ) {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = accept
    if (options?.capture) input.capture = options.capture
    input.multiple = options?.multiple ?? false
    input.style.display = 'none'
    input.onchange = () => {
      const files = Array.from(input.files || [])
      input.remove()
      if (files.length > 0) {
        onPick(files)
        onClose()
      }
    }
    document.body.appendChild(input)
    input.click()
  }

  async function pickFromClipboard() {
    setPasteError(null)
    try {
      const cb = navigator.clipboard
      if (!cb || typeof cb.read !== 'function') {
        setPasteError('Clipboard not available — long-press the composer to paste instead')
        return
      }
      const items = await cb.read()
      const files: File[] = []
      for (const item of items) {
        for (const type of item.types) {
          if (!type.startsWith('image/')) continue
          const blob = await item.getType(type)
          const ext = type.split('/')[1] || 'png'
          files.push(new File([blob], `pasted-${Date.now()}.${ext}`, { type }))
          break
        }
      }
      if (files.length === 0) {
        setPasteError('No image in clipboard. Take a screenshot or long-press an image to copy it.')
        return
      }
      onPick(files)
      onClose()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Clipboard read failed'
      setPasteError(msg.includes('denied')
        ? 'Allow clipboard access in your browser settings to use Paste.'
        : msg)
    }
  }


  return (
    <div
      className="fixed inset-0 z-40 flex flex-col justify-end sm:justify-center sm:items-center bg-black/45"
      role={open ? 'dialog' : undefined}
      aria-modal={open ? 'true' : undefined}
      aria-label={open ? 'Attach' : undefined}
      aria-hidden={!open}
      style={open ? undefined : { display: 'none' }}
      onPointerUpCapture={suppressOpeningGesture}
      onClickCapture={suppressOpeningGesture}
      onClick={closeMenu}
    >
      <div
        onPointerUpCapture={suppressOpeningGesture}
        onClickCapture={suppressOpeningGesture}
        // Stop click bubbling so taps inside the sheet don't dismiss it.
        onClick={(e) => e.stopPropagation()}
        className={clsx(
          'bg-stone-50 dark:bg-stone-900 text-stone-800 dark:text-stone-100',
          // Mobile: slide-up sheet pinned to bottom.
          'rounded-t-3xl sm:rounded-2xl w-full sm:max-w-lg shadow-2xl',
          'border-t border-x border-stone-200 dark:border-stone-700 sm:border',
          '[padding-bottom:max(env(safe-area-inset-bottom),0.75rem)]',
        )}
      >
        {/* Drag-handle bar (purely decorative — phones expect it on
            bottom sheets even though we don't gesture-dismiss yet). */}
        <div className="flex justify-center pt-2 pb-1 sm:hidden">
          <span className="block h-1 w-10 rounded-full bg-stone-300 dark:bg-stone-600" aria-hidden />
        </div>

        <header className="flex h-[60px] items-center gap-2 px-4 py-2">
          <span className="text-sm font-semibold flex-1" role="status">{selectedPhotos === null ? 'Attach' : `${selectedPhotos.length} selected`}</span>
          {selectedPhotos !== null && (
            <>
              <button type="button" disabled={photoLoading} onClick={() => setSelectedPhotos(null)}
                className="min-h-11 px-3 text-sm disabled:opacity-50">Cancel</button>
              <button type="button" disabled={photoLoading || selectedPhotos.length === 0}
                onClick={() => { void attachPhotos(selectedPhotos) }}
                aria-label={`Attach ${selectedPhotos.length} photos`}
                className="min-h-11 shrink-0 rounded-xl bg-blue-600 px-3 text-sm font-semibold text-white disabled:opacity-50">
                {photoLoading ? 'Loading…' : `Attach ${selectedPhotos.length}`}
              </button>
            </>
          )}
          <button
            onClick={closeMenu}
            className="shrink-0 grid place-items-center w-9 h-9 rounded-full text-stone-500 dark:text-stone-400 hover:bg-stone-200 dark:hover:bg-stone-800 active:scale-95 transition"
            aria-label="Close"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        {/* Quick actions row: Camera, Gallery, Video, Paste, File. Each is a
            big square tap target with icon + label. */}
        <div className="grid grid-cols-5 gap-2 px-3 pb-3">
          <ActionTile
            disabled={selectedPhotos !== null || photoLoading}
            icon={<Camera className="w-5 h-5" />}
            label="Camera"
            tone="violet"
            onClick={() => pickViaInput('image/*', { capture: 'environment' })}
          />
          <ActionTile
            disabled={selectedPhotos !== null || photoLoading}
            icon={<ImagesIcon className="w-5 h-5" />}
            label="Gallery"
            tone="blue"
            // image/* only (no video) so Chrome on Android 13+ routes
            // through the system Photo Picker — that's the picker that
            // SHOWS A GRID OF RECENT GALLERY PHOTOS without the
            // OEM-Gallery confirmation step. Mixing image/*,video/* in
            // the accept list demotes Chrome back to the legacy file
            // manager picker on some devices.
            onClick={() => pickViaInput('image/*', { multiple: true })}
          />
          <ActionTile
            disabled={selectedPhotos !== null || photoLoading}
            icon={<Video className="w-5 h-5" />}
            label="Video"
            tone="violet"
            onClick={() => pickViaInput('video/*')}
          />
          <ActionTile
            disabled={selectedPhotos !== null || photoLoading}
            icon={<ClipboardPaste className="w-5 h-5" />}
            label="Paste"
            tone="emerald"
            onClick={pickFromClipboard}
          />
          <ActionTile
            disabled={selectedPhotos !== null || photoLoading}
            icon={<FileText className="w-5 h-5" />}
            label="File"
            tone="amber"
            onClick={() => pickViaInput(FILE_PICKER_ACCEPT, { multiple: true })}
          />
        </div>

        {photoError && <p className="px-4 pb-3 text-sm text-red-700 dark:text-red-300" role="alert">{photoError}</p>}

        {pasteError && (
          <p className="px-4 pb-2 text-[11px] text-amber-700 dark:text-amber-300" role="status">
            {pasteError}
          </p>
        )}

        {/* Device gallery row — Capacitor wrapper only. Inside the
            Android APK, AgentChatGalleryPlugin returns the most recent
            MediaStore images as base64 thumbnails; tapping one fetches
            the full bytes natively and routes through _processFiles.
            Plain PWA users never see this section. */}
        {isNativeGalleryAvailable() && (
          <section className="border-t border-stone-200 dark:border-stone-800 pt-2 pb-3">
            <h3 className="px-4 text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-1.5">
              Gallery
            </h3>
            {galleryDenied ? (
              <div className="px-4 text-[11px] text-stone-500">
                AgentChat doesn't have permission to read your photos.{' '}
                <button
                  type="button"
                  onClick={requestGalleryPermission}
                  className="font-semibold text-blue-600 dark:text-blue-300 underline"
                >
                  Allow access
                </button>
              </div>
            ) : galleryLoading && gallery.length === 0 ? (
              <div className="px-4 text-[11px] text-stone-500">Loading recent photos…</div>
            ) : gallery.length === 0 ? (
              <div className="px-4 text-[11px] text-stone-500">
                No photos found.{galleryDiag ? ` [${galleryDiag}]` : ''}
              </div>
            ) : (
              <div
                className="flex gap-2 overflow-x-auto px-3"
                role="group"
                aria-label="Recent device photos"
              >
                {gallery.map((g) => photoButton(
                  { id: `gallery:${g.id}`, load: () => nativeImageToFile(g) },
                  `data:image/jpeg;base64,${g.thumbBase64}`,
                  g.filename || 'Gallery photo',
                ))}
              </div>
            )}
          </section>
        )}

        {/* Recent strip: clipboard preview (if any) + locally-cached
            previously-sent images. On first-ever use both are empty;
            we surface a hint pointing at the OS share-sheet path
            (Web Share Target) so Mark knows recent screenshots can
            land here directly. */}
        {((clipboard) || (recents && recents.length > 0)) && (
          <section className="border-t border-stone-200 dark:border-stone-800 pt-2 pb-3">
            <h3 className="px-4 text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-1.5">
              Recent
            </h3>
            <div
              className="flex gap-2 overflow-x-auto px-3"
              role="group"
              aria-label="Recent images"
            >
              {clipboard && photoButton(
                { id: 'clipboard', load: async () => new File([clipboard.blob], clipboard.filename, { type: clipboard.mimeType }) },
                clipboard.url, 'Clipboard', true,
              )}
              {recents?.map((r) => photoButton(
                { id: `recent:${r.id}`, load: () => loadRecentImageFile(r.id) },
                r.thumbUrl, r.filename,
              ))}
            </div>
          </section>
        )}
        {!clipboard && recents && recents.length === 0 && (
          <section className="border-t border-stone-200 dark:border-stone-800 px-4 py-3">
            <p className="text-[11px] text-stone-500">
              <strong>Tip:</strong> the browser sandbox can't read your gallery directly.
              Use Android's Share button on a screenshot and pick AgentChat — it'll
              land here ready to send. Recently shared images also appear here for
              one-tap re-attach.
            </p>
          </section>
        )}
      </div>
    </div>
  )
}

function ActionTile({
  icon, label, tone, onClick, disabled = false,
}: {
  icon: React.ReactNode
  label: string
  tone: 'blue' | 'violet' | 'emerald' | 'amber'
  onClick: () => void
  disabled?: boolean
}) {
  const TONES: Record<typeof tone, string> = {
    blue:    'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200',
    violet:  'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200',
    emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
    amber:   'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200',
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="disabled:opacity-50 flex flex-col items-center gap-1.5 py-3 rounded-2xl bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 active:scale-95 transition"
    >
      <span className={clsx('grid place-items-center w-11 h-11 rounded-2xl', TONES[tone])}>
        {icon}
      </span>
      <span className="text-[11px] font-medium">{label}</span>
    </button>
  )
}


export function PhotoButton({ src, alt, selected, disabled, onSelect, onClick, clipboard = false }: {
  src: string
  alt: string
  selected: boolean | undefined
  disabled: boolean
  onSelect: () => void
  onClick: () => void
  clipboard?: boolean
}) {
  const suppressNextClickRef = useRef(false)
  const longPress = useLongPress(() => {
    if (disabled || suppressNextClickRef.current) return
    suppressNextClickRef.current = true
    onSelect()
  })
  return (
    <button
      {...longPress}
      type="button"
      disabled={disabled}
      aria-label={alt}
      aria-pressed={selected}
      onPointerDown={(e) => {
        suppressNextClickRef.current = false
        longPress.onPointerDown(e)
      }}
      onPointerCancel={(e) => longPress.onPointerUp(e)}
      onClick={() => {
        if (suppressNextClickRef.current) {
          suppressNextClickRef.current = false
          return
        }
        onClick()
      }}
      className={clsx(
        'shrink-0 relative w-20 h-20 rounded-xl overflow-hidden border-2 select-none bg-stone-100 dark:bg-stone-800 active:scale-95 transition focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60',
        selected ? 'border-blue-600' : clipboard ? 'border-emerald-500' : 'border-stone-200 dark:border-stone-700',
      )}
      title={alt}
    >
      <img src={src} alt="" className="w-full h-full object-cover pointer-events-none" draggable={false} />
      {clipboard && <span className="absolute bottom-0 inset-x-0 bg-emerald-600/90 text-white text-[9px] font-bold uppercase tracking-wider py-0.5 text-center">Clipboard</span>}
      {selected !== undefined && (
        <span aria-hidden className={clsx('absolute top-1 right-1 grid place-items-center w-6 h-6 rounded-full border-2 border-white shadow', selected ? 'bg-blue-600 text-white' : 'bg-black/30')}>
          {selected && <Check className="w-4 h-4" />}
        </span>
      )}
    </button>
  )
}
